import React, { useMemo, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaDownload, FaRedo, FaStore } from 'react-icons/fa';
import SEO from '../components/SEO';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';

const PLATFORM_FEE_CONFIG = {
  'amazon-india': {
    name: 'Amazon India',
    commissionPercent: 15,
    collectionPercent: 2,
    fixedFeePerUnit: 10,
    shippingFeePerUnit: 45,
    gstOnFeesPercent: 18,
  },
  flipkart: {
    name: 'Flipkart',
    commissionPercent: 18,
    collectionPercent: 2,
    fixedFeePerUnit: 12,
    shippingFeePerUnit: 44,
    gstOnFeesPercent: 18,
  },
  meesho: {
    name: 'Meesho',
    commissionPercent: 0,
    collectionPercent: 2,
    fixedFeePerUnit: 8,
    shippingFeePerUnit: 39,
    gstOnFeesPercent: 18,
  },
};

const EcommerceProfitCalculator = () => {
  const [platform, setPlatform] = useState('amazon-india');
  const [costPrice, setCostPrice] = useState(500);
  const [sellingPrice, setSellingPrice] = useState(799);
  const [quantity, setQuantity] = useState(20);
  const [adSpend, setAdSpend] = useState(500);
  const [extraCostPerUnit, setExtraCostPerUnit] = useState(20);

  const selectedPlatform = PLATFORM_FEE_CONFIG[platform];

  const result = useMemo(() => {
    const cp = Number(costPrice) || 0;
    const sp = Number(sellingPrice) || 0;
    const qty = Number(quantity) || 0;
    const ads = Number(adSpend) || 0;
    const extraUnitCost = Number(extraCostPerUnit) || 0;

    if (qty <= 0) {
      return null;
    }

    const grossRevenue = sp * qty;
    const productCost = cp * qty;
    const extraCostTotal = extraUnitCost * qty + ads;

    const commissionFee = (grossRevenue * selectedPlatform.commissionPercent) / 100;
    const collectionFee = (grossRevenue * selectedPlatform.collectionPercent) / 100;
    const fixedFee = qty * selectedPlatform.fixedFeePerUnit;
    const shippingFee = qty * selectedPlatform.shippingFeePerUnit;

    const platformFeeBase = commissionFee + collectionFee + fixedFee + shippingFee;
    const gstOnPlatformFees = (platformFeeBase * selectedPlatform.gstOnFeesPercent) / 100;
    const totalPlatformCharges = platformFeeBase + gstOnPlatformFees;

    const totalCost = productCost + extraCostTotal + totalPlatformCharges;
    const netProfit = grossRevenue - totalCost;
    const netMarginPercent = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;

    const variableFeeRate =
      (selectedPlatform.commissionPercent + selectedPlatform.collectionPercent) / 100;
    const gstMultiplier = 1 + selectedPlatform.gstOnFeesPercent / 100;
    const fixedChargesTotal = fixedFee + shippingFee;
    const denominator = qty * (1 - variableFeeRate * gstMultiplier);
    const breakEvenUnitPrice =
      denominator > 0
        ? (productCost + extraCostTotal + fixedChargesTotal * gstMultiplier) / denominator
        : null;

    return {
      grossRevenue,
      productCost,
      extraCostTotal,
      commissionFee,
      collectionFee,
      fixedFee,
      shippingFee,
      gstOnPlatformFees,
      totalPlatformCharges,
      totalCost,
      netProfit,
      netMarginPercent,
      breakEvenUnitPrice,
      isProfit: netProfit >= 0,
    };
  }, [adSpend, costPrice, extraCostPerUnit, quantity, selectedPlatform, sellingPrice]);

  const handleReset = () => {
    setPlatform('amazon-india');
    setCostPrice(500);
    setSellingPrice(799);
    setQuantity(20);
    setAdSpend(500);
    setExtraCostPerUnit(20);
  };

  const handleDownloadPDF = () => {
    if (!result) {
      return;
    }

    generateGenericPDF('E-commerce Profit Report', {
      Platform: selectedPlatform.name,
      'Selling Price (per unit)': formatCurrency(sellingPrice),
      'Product Cost (per unit)': formatCurrency(costPrice),
      Quantity: quantity,
      'Ad Spend': formatCurrency(adSpend),
      'Extra Cost (per unit)': formatCurrency(extraCostPerUnit),
      'Gross Revenue': formatCurrency(result.grossRevenue),
      'Total Product Cost': formatCurrency(result.productCost),
      'Total Platform Charges': formatCurrency(result.totalPlatformCharges),
      'Total Cost': formatCurrency(result.totalCost),
      'Net Profit/Loss': formatCurrency(result.netProfit),
      'Net Margin': `${result.netMarginPercent.toFixed(2)}%`,
      'Break-even Selling Price (per unit)': result.breakEvenUnitPrice
        ? formatCurrency(result.breakEvenUnitPrice)
        : 'Not available',
    });
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="E-commerce Profit Calculator"
        description="Free e-commerce profit calculator for Flipkart, Amazon India, and Meesho. Estimate margin after commission, shipping, and GST on fees."
        keywords="ecommerce profit calculator india, flipkart profit calculator, amazon india profit calculator, meesho profit calculator, marketplace margin calculator"
        canonicalPath="/ecommerce-profit"
      />

      <div className="calculator-header">
        <h1><FaStore style={{ color: '#2563eb' }} /> E-commerce Profit Calculator</h1>
        <p>Calculate actual profit margin for Flipkart, Amazon India, and Meesho after charges.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Marketplace</label>
            <div className="tabs">
              {Object.entries(PLATFORM_FEE_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  className={`tab ${platform === key ? 'active' : ''}`}
                  onClick={() => setPlatform(key)}
                >
                  {config.name}
                </button>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label>Selling Price per Unit (Rs.)</label>
            <input
              type="number"
              value={sellingPrice === '' ? '' : sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="1"
            />
          </div>

          <div className="input-group">
            <label>Product Cost per Unit (Rs.)</label>
            <input
              type="number"
              value={costPrice === '' ? '' : costPrice}
              onChange={(e) => setCostPrice(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="1"
            />
          </div>

          <div className="input-group">
            <label>Quantity</label>
            <input
              type="number"
              value={quantity === '' ? '' : quantity}
              onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="500"
                value={quantity || 1}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Total Ad Spend Allocation (Rs.)</label>
            <input
              type="number"
              value={adSpend === '' ? '' : adSpend}
              onChange={(e) => setAdSpend(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
              step="1"
            />
          </div>

          <div className="input-group">
            <label>Other Cost per Unit (Packaging, Handling, etc.) (Rs.)</label>
            <input
              type="number"
              value={extraCostPerUnit === '' ? '' : extraCostPerUnit}
              onChange={(e) =>
                setExtraCostPerUnit(e.target.value === '' ? '' : Number(e.target.value))
              }
              min="0"
              step="1"
            />
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div
                className="result-summary"
                style={{
                  background: result.isProfit
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #ef4444, #dc2626)',
                }}
              >
                <h3>Net {result.isProfit ? 'Profit' : 'Loss'}</h3>
                <div
                  className="amount"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                >
                  {result.isProfit ? <FaArrowUp /> : <FaArrowDown />}
                  {formatCurrency(Math.abs(result.netProfit))}
                </div>
                <small>Net Margin: {result.netMarginPercent.toFixed(2)}%</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Gross Revenue</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.grossRevenue)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Cost</span>
                  <span className="value" style={{ color: '#dc2626' }}>{formatCurrency(result.totalCost)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Product Cost</span>
                  <span className="value">{formatCurrency(result.productCost)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Extra Cost + Ads</span>
                  <span className="value">{formatCurrency(result.extraCostTotal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Marketplace Charges</span>
                  <span className="value" style={{ color: '#dc2626' }}>
                    {formatCurrency(result.totalPlatformCharges)}
                  </span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Break-even Selling Price (per unit)</span>
                  <span className="value">
                    {result.breakEvenUnitPrice ? formatCurrency(result.breakEvenUnitPrice) : 'Not available'}
                  </span>
                </div>
              </div>

              <div className="breakup-section">
                <h4>Charges Breakdown ({selectedPlatform.name})</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Charge Type</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Commission ({selectedPlatform.commissionPercent}%)</td>
                      <td>{formatCurrency(result.commissionFee)}</td>
                    </tr>
                    <tr>
                      <td>Collection Fee ({selectedPlatform.collectionPercent}%)</td>
                      <td>{formatCurrency(result.collectionFee)}</td>
                    </tr>
                    <tr>
                      <td>Fixed Fee</td>
                      <td>{formatCurrency(result.fixedFee)}</td>
                    </tr>
                    <tr>
                      <td>Shipping Fee</td>
                      <td>{formatCurrency(result.shippingFee)}</td>
                    </tr>
                    <tr>
                      <td>GST on Platform Fees ({selectedPlatform.gstOnFeesPercent}%)</td>
                      <td>{formatCurrency(result.gstOnPlatformFees)}</td>
                    </tr>
                    <tr style={{ background: '#fef2f2', fontWeight: 'bold' }}>
                      <td>Total Platform Charges</td>
                      <td style={{ color: '#dc2626' }}>{formatCurrency(result.totalPlatformCharges)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>Notes for Better Accuracy</h4>
        <ul>
          <li>Marketplace rates vary by category, order value, and fulfillment type.</li>
          <li>Use your actual seller panel fee reports for final pricing decisions.</li>
          <li>Ad spend and return orders can materially change real profitability.</li>
          <li>This calculator uses practical average assumptions for quick planning.</li>
        </ul>
      </div>
    </div>
  );
};

export default EcommerceProfitCalculator;